import { SearchInput } from '@/components/common/SearchInput';
import { SubTopNavigationBar } from '@/components/common/SubTopNavigationBar';
import { TopNavigationBar } from '@/components/common/TopNavigationBar';
import Dropdown from '@/components/forecast/Dropdown';
import { SearchButton } from '@/components/common/SearchButton';
import SideLayout from '@/layouts/SideLayout';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExcelExportButton } from '@/components/common/ExcelExportButton';
import { OutlinedButton } from '@/components/common/OutlinedButton';
import { OperationManagementPowerPlantTable } from '@/components/operation-management/OperationManagementPowerPlantTable';
import { testManagementResourceListTable } from '@/utils/data';
import { createPortal } from 'react-dom';
import { OperationManagementPowerPlantRegistrationModal } from '@/components/operation-management/OperationManagementPowerPlantRegistrationModal';
import {
  EnvToggle,
  ResourceManagementEnvironment,
} from '@/components/common/EnvToggle';
import { OperationManagementResourceTable } from '@/components/operation-management/OperationManagementResourceTable';
import { OperationManagementResourceRegistrationModal } from '@/components/operation-management/OperationManagementResourceRegistrationModal';
import { del, get } from '@/utils/fetch';
import { debounce } from 'lodash';

interface OperationManagementPageProps {
  testId?: string;
  className?: string;
}

enum ResourceFilter {
  Default,
  Single,
  Collection,
}

export const OperationManagementPage: React.FC<
  OperationManagementPageProps
> = ({ testId, className }) => {
  const [managementGenerationData, setManagementGenerationData] = useState<
    Array<MapCurrentEntity>
  >([]);

  const getSiteTypeFromDerResType = useCallback(
    (key: string | null | undefined) => {
      switch (key) {
        case 'individual':
          return ResourceFilter.Single;
        case 'set':
          return ResourceFilter.Collection;
        default:
          return ResourceFilter.Default;
      }
    },
    []
  );

  const testManagementGenerationListTable = useMemo(() => {
    return managementGenerationData.map((data) => {
      const {
        cuid: resourceCode,
        longitude,
        latitude,
        siteCapacity: generationCapacity,
        address,
        genNum,
        cuidName: resourceName,
        class: siteClass,
        siteId: energyMarketId,
        derResType,
        kpxId,
        siteName: plantName,
        siteId,
        plants,
        contactPerson,
      } = data;

      return {
        resourceName,
        resourceCode,
        plantName,
        siteClass,
        kpxId,
        energyMarketId,
        generatorCount: genNum,
        generationCapacity,
        coordinates: `${latitude}, ${longitude}`,
        location: address,
        siteType: getSiteTypeFromDerResType(derResType),
        siteId,
        plants,
        contactPerson,
      };
    });
  }, [managementGenerationData]);

  const { t } = useTranslation();
  const [resourceFilter, setResourceFilter] = useState(ResourceFilter.Default);
  const [generationFilter, setGenerationFilter] = useState<string | null>(null);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [resourceSearchKeyword, setResourceSearchKeyword] =
    useState<string>('');
  const [filterKeyword, setFilterKeyword] = useState<string>('');
  const [resourceFilterKeyword, setReosurceFilterKeyword] =
    useState<string>('');
  const [selectedNavigationIndex] = useState(0);
  const [selectedSubNavigationIndex, setSelectedSubNavigationIndex] =
    useState(0);
  const [showPowerPlantRegistrationModal, setShowPowerPlantRegistrationModal] =
    useState<{
      isShow: boolean;
      data: null | PowerPlantListTableData;
      isEdit: boolean;
    }>({ isShow: false, data: null, isEdit: false });
  const [showResourceRegistrationModal, setShowResourceRegistrationModal] =
    useState(false);
  const filteredData = useMemo(() => {
    return testManagementGenerationListTable
      .filter((data) =>
        resourceFilter === ResourceFilter.Default
          ? true
          : data.siteType === resourceFilter
      )
      .filter((data) =>
        generationFilter === null ? true : data.plantName === generationFilter
      )
      .filter((data) => data.plantName.includes(filterKeyword));
  }, [
    testManagementGenerationListTable,
    resourceFilter,
    generationFilter,
    filterKeyword,
  ]);
  const resourceFilteredData = useMemo(() => {
    return testManagementResourceListTable.filter((data) =>
      data.resourceName.includes(resourceFilterKeyword)
    );
  }, [testManagementResourceListTable, resourceFilterKeyword]);
  const generationList = useMemo(() => {
    return filteredData.reduce((prev, cur) => {
      const { plantName } = cur;
      if (prev.every((data) => data !== plantName)) prev.push(plantName);

      return prev;
    }, [] as Array<string>);
  }, [filteredData]);

  const topNavigationBarMenus = [
    {
      text: 'management.navigation.resourceDataManagement',
      context: 'operation',
    },
    {
      text: 'management.navigation.searchGroupManagement',
      context: 'operation',
    },
    { text: 'management.navigation.mlManagement', context: 'operation' },
  ];
  const subTopNavigationBarMenus = [
    {
      text: 'management.navigation.powerPlantManagement',
      context: 'operation',
    },
    {
      text: 'management.navigation.resourceManagement',
      context: 'operation',
    },
  ];

  const [operationManagementPageRef, setOperationManagementPageRef] =
    useState<HTMLDivElement | null>(null);

  const getResourceFilterTranslation = (filter: ResourceFilter) => {
    switch (filter) {
      case ResourceFilter.Default:
        return t('management.selectResource');
      case ResourceFilter.Single:
        return t('management.powerPlant.singleResource');
      case ResourceFilter.Collection:
        return t('management.powerPlant.collectionResource');
    }
  };

  const handleChange = (text: string) => {
    setSearchKeyword(text);
  };

  const handleSearchButtonClick = () => {
    setFilterKeyword(searchKeyword);
  };

  const resourceSearchHandleChange = (text: string) => {
    setResourceSearchKeyword(text);
  };

  const handleResourceSearchButtonClick = () => {
    setReosurceFilterKeyword(resourceSearchKeyword);
  };

  const debouncedFetchSiteName = useCallback(
    debounce(
      () =>
        get<{ data: Array<MapCurrentEntity> }>('/customer/map/current').then(
          (response) => {
            if (response.status === 'success') {
              setManagementGenerationData(response.data);
            }
          }
        ),
      1000
    ),
    [get]
  );

  const debouncedDeleteSiteName = useCallback(
    debounce(
      (siteId: string) =>
        del<void>('/customer/site', {
          params: {
            siteId,
          },
        }).then((response) => {
          if (response.status === 'success') {
            return debouncedFetchSiteName();
          }
        }),
      1000
    ),
    [del]
  );

  useEffect(() => {
    debouncedFetchSiteName();
  }, []);

  return (
    <SideLayout ref={setOperationManagementPageRef}>
      <div data-testid={testId} className={`${className}`}>
        <TopNavigationBar
          testId="operation-management-page-top-navigation"
          selectedIndex={selectedNavigationIndex}
          menus={topNavigationBarMenus}
        />
        <SubTopNavigationBar
          testId="operation-management-page-sub-top-navigation"
          selectedIndex={selectedSubNavigationIndex}
          menus={subTopNavigationBarMenus}
          setNavigationIndex={setSelectedSubNavigationIndex}
        />
      </div>
      {selectedSubNavigationIndex === 0 ? (
        <section className="px-[29px] py-[25px]">
          <p
            data-testid="operation-management-page-plant-management-title"
            className="noto-15-medium leading-[22px]"
          >
            {t('management.powerPlant.title', {
              context: 'operation',
            })}
          </p>
          <div className="mt-4 flex">
            <div className="flex items-center">
              {/* 자원 선택 */}
              <Dropdown
                testId="operation-management-page-plant-management-resource-dropdown"
                items={[
                  getResourceFilterTranslation(ResourceFilter.Single),
                  getResourceFilterTranslation(ResourceFilter.Collection),
                ]}
                className="w-[200px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
                selectedItem={getResourceFilterTranslation(resourceFilter)}
                onSelected={(text: string) => {
                  const matchedFilter = Object.values(ResourceFilter)
                    .filter((v): v is ResourceFilter => typeof v === 'number')
                    .find(
                      (filter) => getResourceFilterTranslation(filter) === text
                    );
                  setResourceFilter(
                    matchedFilter !== undefined
                      ? (matchedFilter as ResourceFilter)
                      : ResourceFilter.Default
                  );
                }}
              ></Dropdown>
              {/* 발전소 선택 */}
              <Dropdown
                testId="operation-management-page-plant-management-plant-dropdown"
                items={generationList}
                className="ml-[10px] w-[200px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
                selectedItem={
                  generationFilter === null
                    ? t('management.selectPlant')
                    : generationFilter
                }
                onSelected={(text: string) => {
                  const filter = generationList.some(
                    (generation) => generation === text
                  )
                    ? text
                    : null;
                  setGenerationFilter(filter);
                }}
              ></Dropdown>
              <SearchInput
                className="ml-[10px] w-[320px]"
                testId="operation-management-page-plant-management-search-input"
                handleChange={handleChange}
                text={searchKeyword}
              />
              <SearchButton
                className="ml-[10px]"
                testId="operation-management-page-plant-management-search-button"
                handleClick={handleSearchButtonClick}
              />
            </div>
            <div className="ml-auto flex items-center">
              <ExcelExportButton testId="operation-management-page-plant-management-excel-button" />
              <OutlinedButton
                onClick={() =>
                  setShowPowerPlantRegistrationModal({
                    isShow: true,
                    data: null,
                    isEdit: false,
                  })
                }
                testId="operation-management-page-plant-management-add-plant-button"
                className="ml-[15px] h-[36px] w-[150px] [&>p]:my-[6px]"
                text={t('management.registerPlant')}
                isClicked={false}
              />
              {showPowerPlantRegistrationModal.isShow &&
                operationManagementPageRef &&
                createPortal(
                  <div className="z-100 absolute left-0 top-0 flex h-full w-full items-center justify-center bg-black bg-opacity-80">
                    <OperationManagementPowerPlantRegistrationModal
                      testId="operation-management-page-power-plant-registration-modal"
                      onClick={() =>
                        setShowPowerPlantRegistrationModal({
                          isShow: false,
                          data: null,
                          isEdit: false,
                        })
                      }
                      navigationBarIndex={
                        showPowerPlantRegistrationModal.isEdit ? 1 : 0
                      }
                      isEdit={showPowerPlantRegistrationModal.isEdit}
                      data={showPowerPlantRegistrationModal.data}
                      className="min-h-[510px] min-w-[626px]"
                    />
                  </div>,
                  operationManagementPageRef
                )}
            </div>
          </div>
          <OperationManagementPowerPlantTable
            className="mt-[17px]"
            data={filteredData}
            testId="operation-management-page-plant-management-generation-list-table"
            handleEdit={(data) => {
              setShowPowerPlantRegistrationModal({
                isShow: true,
                data,
                isEdit: true,
              });
            }}
            handleDelete={debouncedDeleteSiteName}
          />
        </section>
      ) : (
        <section className="px-[29px] py-[25px]">
          <p
            data-testid="operation-management-page-resource-management-title"
            className="noto-15-medium leading-[22px]"
          >
            {t('management.resource.title', {
              context: 'operation',
            })}
          </p>
          <EnvToggle
            className="mt-[17px]"
            testId="operation-management-page-resource-management-env-toggle"
            selectedToggle={ResourceManagementEnvironment.Production}
          />
          <div className="mt-4 flex items-center">
            <SearchInput
              className="w-[320px]"
              testId="operation-management-page-resource-management-search-input"
              handleChange={resourceSearchHandleChange}
              text={resourceSearchKeyword}
            />
            <SearchButton
              className="ml-[10px]"
              testId="operation-management-page-resource-management-search-button"
              handleClick={handleResourceSearchButtonClick}
            />
            <OutlinedButton
              onClick={() => setShowResourceRegistrationModal(true)}
              testId="operation-management-page-resource-management-register-button"
              className="ml-auto h-[36px] w-[150px] [&>p]:my-[6px]"
              text={t('management.resource.registerResource')}
              isClicked={false}
            />
            {showResourceRegistrationModal &&
              operationManagementPageRef &&
              createPortal(
                <div className="z-100 absolute left-0 top-0 flex h-full w-full items-center justify-center bg-black bg-opacity-80">
                  <OperationManagementResourceRegistrationModal
                    testId="operation-management-page-resource-registration-modal"
                    onClick={() => setShowResourceRegistrationModal(false)}
                    className="min-h-[510px] min-w-[626px]"
                  />
                </div>,
                operationManagementPageRef
              )}
          </div>
          <OperationManagementResourceTable
            className="mt-[17px]"
            data={resourceFilteredData}
            testId="operation-management-page-resource-management-resource-list-table"
          />
        </section>
      )}
    </SideLayout>
  );
};
